import { useMemo } from 'react';

import { ColumnFiltersState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DateRangeType } from 'application-types';
import { FilterType, NativeFilterTypes } from 'design-system-web';
import { InspectionInstanceStatus } from 'graphql-query-contracts';
import { useGetInspectionInstanceFilterAttributes } from 'react-hooks';

import {
  FILTERABLE_INSPECTION_STATUSES,
  STATUS_TRANSLATION_KEYS,
} from '../constants';

const FILTER_KEYS = {
  inspectionName: 'inspectionName',
  completedBy: 'completedByName',
  status: 'status',
  startedOn: 'startedAt',
  completedOn: 'completedAt',
};

export interface InspectionsDashboardFilterValues {
  status?: InspectionInstanceStatus;
  completedBy?: string;
  templateNameSearch?: string;
  startedOn?: DateRangeType;
  completedOn?: DateRangeType;
}

const parseString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const parseStatus = (value: unknown): InspectionInstanceStatus | undefined =>
  Object.values(InspectionInstanceStatus).find(status => status === value);

const parseDateRange = (value: unknown): DateRangeType | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }

  const startDate =
    'startDate' in value && value.startDate instanceof Date
      ? value.startDate
      : undefined;
  const endDate =
    'endDate' in value && value.endDate instanceof Date
      ? value.endDate
      : undefined;

  if (!startDate && !endDate) {
    return undefined;
  }

  return { startDate, endDate };
};

export function parseDashboardFilters(
  activeFilters: ColumnFiltersState
): InspectionsDashboardFilterValues {
  const values = new Map(activeFilters.map(f => [f.id, f.value]));

  return {
    status: parseStatus(values.get(FILTER_KEYS.status)),
    completedBy: parseString(values.get(FILTER_KEYS.completedBy)),
    templateNameSearch: parseString(values.get(FILTER_KEYS.inspectionName)),
    startedOn: parseDateRange(values.get(FILTER_KEYS.startedOn)),
    completedOn: parseDateRange(values.get(FILTER_KEYS.completedOn)),
  };
}

export function hasActiveDashboardFilters(
  values: InspectionsDashboardFilterValues
): boolean {
  return Object.values(values).some(v => v !== undefined);
}

export const useInspectionsDashboardFilters = (
  channelId: string,
  timeZone?: string
) => {
  const { t } = useTranslation();
  const { completers, templateNames } = useGetInspectionInstanceFilterAttributes(
    { channelId }
  );

  const filters = useMemo<FilterType[]>(
    () => [
      {
        key: FILTER_KEYS.inspectionName,
        label: t('web.admin.inspections.filter.inspectionName'),
        type: NativeFilterTypes.Select,
        options: templateNames.map(name => ({ label: name, value: name })),
        doTranslation: false,
        isPromoted: true,
      },
      {
        key: FILTER_KEYS.completedBy,
        label: t('web.admin.inspections.filter.completedBy'),
        type: NativeFilterTypes.Select,
        options: completers.map(c => ({ label: c.name, value: c.id })),
        doTranslation: false,
        isPromoted: true,
      },
      {
        key: FILTER_KEYS.status,
        label: t('web.admin.inspections.filter.status'),
        type: NativeFilterTypes.Select,
        options: FILTERABLE_INSPECTION_STATUSES.map(value => ({
          label: t(STATUS_TRANSLATION_KEYS[value]),
          value,
        })),
        isPromoted: true,
      },
      {
        key: FILTER_KEYS.startedOn,
        label: t('web.admin.inspections.filter.startedOn'),
        type: NativeFilterTypes.DateRange,
        isPromoted: true,
        timeZone,
      },
      {
        key: FILTER_KEYS.completedOn,
        label: t('web.admin.inspections.filter.completedOn'),
        type: NativeFilterTypes.DateRange,
        isPromoted: true,
        timeZone,
      },
    ],
    [t, completers, templateNames, timeZone]
  );

  return { filters };
};
